import { useEffect, useState } from "react";
import { Loader2, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTES } from "@/lib/routes";

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

export const AdminProtectedRoute = ({ children }: AdminProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [showUnauthorized, setShowUnauthorized] = useState(false);

  // useTrackUnauthorizedAdminAccess(); // This hook relies on old user.role structure

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate(ROUTES.AUTH, { replace: true, state: { from: window.location.pathname } });
      return;
    }

    // Better Auth stores roles in user.role
    if (user.role === "admin") {
      return;
    }

    setShowUnauthorized(true);
    console.warn(`⚠️ Non-admin user ${user.id} (${user.email}) with role '${user.role}' attempted to access admin panel`);
    
    setTimeout(() => {
      if (user.role === 'waitlisted') {
        navigate(ROUTES.WAITLIST, { replace: true });
      } else {
        navigate(ROUTES.DASHBOARD, { replace: true });
      }
    }, 3000);
  }, [user, authLoading, navigate]);

  if (showUnauthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Alert variant="destructive" className="max-w-md">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You do not have permission to access the admin panel. This attempt has been logged.
            Redirecting...
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
